import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SchoolFees() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ grade: "Grade 1", term: "Term 1 2026", amount: 150000, currency: "NGN" });
  const [confirm, setConfirm] = useState(null); // fee object pending deletion

  const reload = () => api.get("/schools/fee-structure").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/schools/fee-structure", form); toast.success("Fee added"); reload(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/schools/fee-structure/${confirm.id}`);
      toast.success("Fee structure deleted");
      setConfirm(null);
      reload();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <DashboardShell title="Fee structure" subtitle="Define amounts per grade and term.">
      <form onSubmit={add} className="card mb-6" data-testid="fee-structure-form">
        <h3 className="font-display text-lg font-medium mb-4">Add fee</h3>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Grade"><input className="input mt-1" required value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} data-testid="fee-grade-input" /></Field>
          <Field label="Term"><input className="input mt-1" required value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} data-testid="fee-term-input" /></Field>
          <Field label="Amount (₦)"><input type="number" className="input mt-1" required min="1000" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="fee-amount-input" /></Field>
          <div className="flex items-end"><button type="submit" className="btn-primary w-full justify-center" data-testid="fee-save-button"><Plus size={14} weight="bold" /> Save</button></div>
        </div>
      </form>
      <div className="card">
        {list.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-8 text-center">No fees set.</p>
        ) : (
          <table className="dense">
            <thead><tr><th>Grade</th><th>Term</th><th>Amount</th><th></th></tr></thead>
            <tbody>
              {list.map((f) => (
                <tr key={f.id} data-testid={`fee-row-${f.id}`}>
                  <td className="font-medium">{f.grade}</td>
                  <td>{f.term}</td>
                  <td className="font-mono">{fmtNGN(f.amount)}</td>
                  <td className="text-right">
                    <button onClick={() => setConfirm(f)} className="btn-ghost text-xs text-[#C93B3B] hover:!bg-[#FBE3E3]" data-testid={`fee-delete-${f.id}`}>
                      <Trash size={14} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setConfirm(null)} data-testid="fee-delete-modal">
          <div className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl border border-[#E5E3DB]" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-[#FBE3E3] grid place-items-center mb-4">
              <Warning size={24} weight="fill" className="text-[#C93B3B]" />
            </div>
            <h3 className="font-display text-2xl font-medium text-[#132A13]">Delete this fee structure?</h3>
            <p className="text-sm text-[#4F5D4F] mt-2">
              You're about to permanently delete <span className="font-semibold text-[#132A13]">{confirm.grade} · {confirm.term} · {fmtNGN(confirm.amount)}</span>.
            </p>
            <p className="text-sm text-[#C93B3B] mt-3 bg-[#FBE3E3] rounded-lg p-3">
              <strong>Warning:</strong> Parents linked to students in this grade will no longer see an outstanding fee until you create a new structure. This cannot be undone.
            </p>
            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => setConfirm(null)} className="btn-ghost text-sm" data-testid="fee-delete-cancel">Cancel</button>
              <button onClick={doDelete} className="btn-primary !bg-[#C93B3B] hover:!bg-[#a72c2c] text-sm" data-testid="fee-delete-confirm">
                <Trash size={14} weight="bold" /> Yes, delete it
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}</span>{children}</label>;
}
